import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { getBillingClient } from './getBillingClient'
import type { ReactNode } from 'react'
import type { BillingClient } from './IBillingClient'
import type { BillingProviderPublicConfig } from '@/lib/api/features/billing/getBillingConfig'

interface BillingContextValue {
  client: BillingClient | null
  providerName: BillingProviderPublicConfig['provider'] | null
  environment: BillingProviderPublicConfig['environment'] | null
  isLoading: boolean
  error: Error | null
}

const BillingContext = createContext<BillingContextValue | undefined>(undefined)

/**
 * Provides the active billing client + public config to the subscription UI.
 * Wraps the routes that need billing — typically just the subscription
 * settings tree — so the upfront /billing/config fetch only fires when
 * the user actually navigates there.
 */
export function BillingProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<BillingContextValue>({
    client: null,
    providerName: null,
    environment: null,
    isLoading: true,
    error: null,
  })

  useEffect(() => {
    let cancelled = false
    getBillingClient()
      .then(({ client, config }) => {
        if (cancelled) return
        setState({
          client,
          providerName: config.provider,
          environment: config.environment,
          isLoading: false,
          error: null,
        })
      })
      .catch((error: Error) => {
        if (cancelled) return
        setState((prev) => ({ ...prev, isLoading: false, error }))
      })
    return () => {
      cancelled = true
    }
  }, [])

  const value = useMemo(() => state, [state])
  return (
    <BillingContext.Provider value={value}>{children}</BillingContext.Provider>
  )
}

export function useBilling(): BillingContextValue {
  const ctx = useContext(BillingContext)
  if (!ctx) {
    throw new Error('useBilling must be used inside a <BillingProvider>')
  }
  return ctx
}
